import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CommentPosition as AbsoluteCommentPosition,
  ListOfCommentReplies,
  ListOfRootComments,
  Post,
  ReplyComment,
  ReplyCommentRequest,
  DefaultService as RestApiService,
  RootComment,
} from 'src/app/swagger-api';
import { CommentsBaseService } from './comments/comments.base.service';
import { CommentsContentProviderService } from './comments/comments.content-provider.service';

@Injectable({ providedIn: 'root' })
export class PostDetailCommentsService extends CommentsBaseService implements CommentsContentProviderService {
  // We can have an alternative where this class variable is removed and we pass explicit params to each method?
  // It would be a little bit more verbose, but we could eliminate some side effects
  private postId = '';

  private DEFAULT_COMMENTS_LIMIT: number = CommentsBaseService.COMMENTS_PER_PAGE;
  private DEFAULT_REPLY_LIMIT: number = CommentsBaseService.COMMENT_REPLIES_PER_PAGE;

  private apiService = inject(RestApiService);

  public setPostInformation(post: Post): void {
    this.postId = post.entity_id;
    this.canViewComments = post.permissions.view_comments;
  }

  public queryInitialComments(commentLimit?: number, replyLimit?: number): Observable<ListOfRootComments> {
    return this.apiService.restSocialPostCommentsIndexList(
      this.postId,
      0,
      commentLimit ? commentLimit : this.DEFAULT_COMMENTS_LIMIT,
      this.commentsSortingStrategy,
      this.replySortingStrategy,
      replyLimit ? replyLimit : this.DEFAULT_REPLY_LIMIT,
    );
  }

  public queryMoreRootComments(rootCommentOffset: number): Observable<ListOfRootComments> {
    return this.apiService.restSocialPostCommentsIndexList(
      this.postId,
      rootCommentOffset,
      CommentsBaseService.COMMENTS_PER_PAGE,
      this.commentsSortingStrategy,
      this.replySortingStrategy,
      CommentsBaseService.COMMENT_REPLIES_PER_PAGE,
    );
  }

  public queryRemainingRootComments(rootCommentOffset: number): Observable<ListOfRootComments> {
    return this.apiService.restSocialPostCommentsIndexList(
      this.postId,
      rootCommentOffset,
      undefined,
      this.commentsSortingStrategy,
      this.replySortingStrategy,
      CommentsBaseService.COMMENT_REPLIES_PER_PAGE,
    );
  }

  public queryMoreReplyComments(rootCommentId: string, replyOffset: number): Observable<ListOfCommentReplies> {
    return this.apiService.restCommentReplyIndexPaginable(
      rootCommentId,
      replyOffset,
      CommentsBaseService.COMMENT_REPLIES_PER_PAGE,
      this.replySortingStrategy,
    );
  }

  public queryRemainingReplyComments(rootCommentId: string, replyOffset: number): Observable<ListOfCommentReplies> {
    return this.apiService.restCommentReplyIndexPaginable(rootCommentId, replyOffset, null, this.replySortingStrategy);
  }

  public executeCreateComment(text: string): Observable<RootComment> {
    return this.apiService.restSocialPostCommentsPost({ text }, this.postId);
  }

  public executeReplyComment(
    rootCommentId: string,
    replyText: string,
    repliedCommentId?: string,
  ): Observable<ReplyComment> {
    const replyCommentRequest: ReplyCommentRequest = repliedCommentId
      ? { text: replyText, replied_comment_id: repliedCommentId }
      : { text: replyText };

    return this.apiService.restCommentReplyPost(replyCommentRequest, rootCommentId);
  }

  public executeDeleteComment(commentId: string): Observable<void> {
    return this.apiService.restCommentsDelete(commentId) as Observable<void>;
  }

  public queryCommentPosition(commentId: string): Observable<AbsoluteCommentPosition> {
    return this.apiService.restCommentPositionGet(commentId, this.commentsSortingStrategy, this.replySortingStrategy);
  }

  protected contentProvider(): CommentsContentProviderService {
    return this;
  }
}
